import React, { Fragment, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from '../../common/react-redux-hooks';
import GenericDHTableView from '../../common/GenericDHTable/GenericDHTableView';
import {
  CAC_CLUSTER_SEGMENT_INDEX,
  CAC_CLUSTER_SEGMENT_MEAN,
  CAC_CLUSTER_SEGMENT_STANDARD_DEVIATION,
  CAC_CLUSTER_SEGMENT_ABSOLUTE_DEVIATION,
  CAC_CLUSTER_ACTIVE_TABLE,
  CAC_CLUSTER_TABLE_TYPE,
  CAC_CLUSTER_SEG_COMPARISON_TITLE,
} from '../CacConstants';
import {
  getClusterSegmentTableData,
  getSegmentTableHeaders,
  CAC_CLUSTER_SEG_FIRST_COL_HEADER_LABELS,
  getSegmentMainHeaderLabels,
  SORT_DIRECTION,
  sortCACTable,
} from '../../../util/cacUtils';
import CacDropButtons from '../enhancedFeatures/CacDropButtons';
import InfoBox, {
  InfoBoxSpacer,
} from '../../common/EnhancedGridFeature/InfoBox';
import downArrow from '../../../images/corrcluster/downArrow.svg';
import { updateSortOrder } from '../../../actions/cacActions';

const ClusterSegmentComparison = props => {
  const {
    project: {
      activeTable: { clusterActiveTable },
      clusterSegmentCount,
      selectedCluster,
      clusterResponse,
      sortData,
    },
  } = props;
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState([]);
  const [dropPanels, setDropPanels] = useState([]);
  const [compareValue, setCompareValue] = useState(CAC_CLUSTER_SEGMENT_INDEX);
  const infoBoxRef = useRef(null);
  const solutions = clusterResponse ? clusterResponse.solutions : [];
  const segmentData = selectedCluster > -1 ? solutions[selectedCluster].segments : undefined;
  const statements = clusterResponse ? clusterResponse.targets : [];
  const sortObj = sortData.clusterTableType[clusterActiveTable];
  const { sortOrder, key, columnIndex } = sortObj;
  let tableArr = [];

  const tableDataArgs = {
    segmentData,
    statements,
    compareValue: compareValue.value,
  };

  const handleSort = sortObj => {
    const payload = {
      tableType: CAC_CLUSTER_ACTIVE_TABLE,
      module: CAC_CLUSTER_TABLE_TYPE,
      sortObj,
    };
    dispatch(updateSortOrder(payload));
  };

  useEffect(() => {
    if (segmentData && statements) {
      const cellElements = getSegmentTableHeaders(
        segmentData,
        clusterSegmentCount
      );
      tableArr = getClusterSegmentTableData(tableDataArgs);

      if (sortObj.sortOrder && sortObj.sortOrder !== SORT_DIRECTION.NONE) {
        tableArr = sortCACTable(sortObj, tableArr, cellElements);
      }
    }
    setTableData(tableArr);
  }, [
    JSON.stringify(sortObj),
    JSON.stringify(tableArr),
    JSON.stringify(props),
    JSON.stringify(compareValue)
  ]);

  const buildInfoBox = () => {
    if (dropPanels.length === 0) {
      const ibp = [];
      ibp.push(
        <CacDropButtons
          label={CAC_CLUSTER_SEGMENT_INDEX.label}
          infoBoxRef={infoBoxRef}
          setCompareValue={setCompareValue}
          hoverValue= {CAC_CLUSTER_SEGMENT_INDEX.hoverValue}
        />
      );
      ibp.push(
        <InfoBoxSpacer Height="0.1em" boxClassName="toolPalette-spacer-row" />
      );
      ibp.push(
        <CacDropButtons
          label={CAC_CLUSTER_SEGMENT_MEAN.label}
          infoBoxRef={infoBoxRef}
          setCompareValue={setCompareValue}
          hoverValue= {CAC_CLUSTER_SEGMENT_MEAN.hoverValue}
        />
      );
      ibp.push(
        <InfoBoxSpacer Height="0.1em" boxClassName="toolPalette-spacer-row" />
      );
      ibp.push(
        <CacDropButtons
          label={CAC_CLUSTER_SEGMENT_STANDARD_DEVIATION.label}
          infoBoxRef={infoBoxRef}
          setCompareValue={setCompareValue}
          hoverValue= {CAC_CLUSTER_SEGMENT_STANDARD_DEVIATION.hoverValue}
        />
      );
      ibp.push(
        <InfoBoxSpacer Height="0.1em" boxClassName="toolPalette-spacer-row" />
      );
      ibp.push(
        <CacDropButtons
          label={CAC_CLUSTER_SEGMENT_ABSOLUTE_DEVIATION.label}
          infoBoxRef={infoBoxRef}
          setCompareValue={setCompareValue}
          hoverValue= {CAC_CLUSTER_SEGMENT_ABSOLUTE_DEVIATION.hoverValue}
        />
      );
      setDropPanels(ibp);
    }
    return dropPanels;
  };

  const comparisonSelector = () => {
    return (
      <div className="segment-comparsion-selection">
        <div className="segment-comparsion-selection-infobox">
          <span>{compareValue.label}</span>
          <InfoBox
            ref={infoBoxRef}
            Panels={buildInfoBox()}
            updatePanels={buildInfoBox}
            Positions={['bottom']}
            Align="end"
            InfoBoxButtonInner={
              <img className="ibox-downArrow" src={downArrow} alt="v" />
            }
            containerClassName="ib-popover-dd-container"
          />
        </div>
      </div>
    );
  };

  const tableView = () => {
    if (segmentData && statements) {
      return (
        <Fragment>
          <p className="segment-comparison-title"> {CAC_CLUSTER_SEG_COMPARISON_TITLE} </p>
          <div className="generic-table-main-wrapper">
            <div className="row expanded">
              {comparisonSelector()}
              <div className="columns large-12 generic-table-child-wrapper-left">
                <div className="row expanded top-controls-wrapper">
                  <div className="row expanded generic-table">
                    <div className="columns large-12 padding-left-0 padding-right-0">
                      <GenericDHTableView
                        data-test="dhTable"
                        tableData={tableData}
                        firstHeaderCol={CAC_CLUSTER_SEG_FIRST_COL_HEADER_LABELS}
                        firstHeaderColData={tableData.map(item => item.statement)}
                        mainHeaderCol={getSegmentMainHeaderLabels(
                          clusterSegmentCount
                        )}
                        columnHeaders={getSegmentTableHeaders(
                          segmentData,
                          clusterSegmentCount
                        )}
                        showMainHeader={false}
                        isClusterSegTable
                        onSort={handleSort}
                        sortColumnIndex={columnIndex}
                        sortKey={key}
                        sortOrder={sortOrder}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      );
    }
    return null;
  };

  return <div className="segment-comparison-block">{tableView()}</div>;
};

ClusterSegmentComparison.propTypes = {
  project: PropTypes.shape({
    clusterResponse: PropTypes.shape({
      solutions: PropTypes.instanceOf(Array),
      targets: PropTypes.instanceOf(Array),
    }),
    sortData: PropTypes.shape({
      clusterTableType: PropTypes.instanceOf(Object),
    }),
    activeTable: PropTypes.shape({
      clusterActiveTable: PropTypes.string,
    }),
    selectedCluster: PropTypes.number,
    clusterSegmentCount: PropTypes.number,
  }),
  clusterActiveTable: PropTypes.string,
  columnIndex: PropTypes.number,
  sortOrder: PropTypes.string,
  key: PropTypes.string,
};

export default ClusterSegmentComparison;
